import { metricsEventsMap, MetricsType } from 'metrics/types';
import { del, download, get, patch, post, put } from './request';

export function foldersGet(
  params: API$FoldersParams
): Promise<{ pages: AH$Pages; payload: Array<API$Folder> }> {
  return get('/api/v6/folders/', params);
}

export function folderPatch(
  folderId: number,
  data: API$FolderData
): Promise<{ payload: API$Folder }> {
  return patch(`/api/v6/folders/${folderId}/`, { data });
}

export function folderGet(
  folderId: number
): Promise<{ candidate_statuses: Array<API$Status>; payload: API$Folder; profileReferer: string }> {
  return get(`/api/v6/folders/${folderId}/`);
}

export function folderCreate(data: API$FolderData): Promise<{ payload: API$Folder }> {
  return post('/api/v6/folders/', { data });
}

export function folderDelete(folderId: number, params?: API$SaveMessagingHistory): Promise<void> {
  return del(`/api/v6/folders/${folderId}/`, params);
}

export function statusesGet(): Promise<{ payload: Array<API$Status> }> {
  return get('/api/internal/status_collection/statuses/');
}

export function statusEdit(
  statusId: number,
  data: API$StatusData
): Promise<{ payload: API$Status }> {
  return patch(`/api/internal/status_collection/statuses/${statusId}/`, { data });
}

export function statusDelete(statusId: number): Promise<void> {
  return del(`/api/internal/status_collection/statuses/${statusId}/`);
}

export function statusCreate(data: API$StatusData): Promise<{ payload: API$Status }> {
  return post('/api/internal/status_collection/statuses/', { data });
}

export function statusesOrderChange(data: Array<number>): Promise<{ payload: Array<API$Status> }> {
  return post('/api/internal/status_collection/change_order/', { data });
}

export function candidatesPost(
  data: Array<API$CandidateData>
): Promise<{ payload: { invalidCount: number; successCount: number } }> {
  return post('/api/v6/candidates/', { data });
}

export function candidatePatch(
  candidateId: number,
  statusId: number
): Promise<{ payload: API$Candidate }> {
  return patch(`/api/v6/candidates/${candidateId}/`, { data: { status: statusId } });
}

export function massCandidatesPatch(
  candidateIds: Array<number>,
  statusId: number
): Promise<{ payload: Array<API$Candidate> }> {
  return patch('/api/v6/candidates/', {
    params: { ids: candidateIds },
    data: { status: statusId },
  });
}

export function candidatesDelete(params: API$DeleteCandidatesParams): Promise<void> {
  return del('/api/v6/candidates/', params);
}

export function candidatesGet(params: API$CandidatesParams): Promise<{
  payload: API$PaginatedResponse<API$Candidate>;
}> {
  return get('/api/v6/candidates/', params);
}

export function folderCandidatesGet(
  folderId: number,
  params?: API$CandidatesParams
): Promise<{
  payload: API$PaginatedResponse<API$Candidate> & {
    candidate_statuses: Array<API$Status>;
    profiles_events: {
      [key: number]: API$ProfileEvent[];
    };
  };
}> {
  return get(`/api/v6/folders/${folderId}/candidates/`, params);
}

export function addAllCandidateToFolder(
  rawQuery: string
): Promise<{ payload: { candidates_count: number; id: number; name: string } }> {
  return get(`/api/internal/folders/from_search_query${rawQuery}`);
}

export function commentsGet(
  id: number,
  params?: { general?: boolean }
): Promise<{ payload: Array<API$Comment> }> {
  return get(`/api/internal/profiles/${id}/comments/`, params);
}

export function accountLinkGet(id: number, hash: string): Promise<{ payload: { link: string } }> {
  return get(`/api/profiles/${id}/links/${hash}/`);
}

export function commentsAdd(
  id: number,
  text: string,
  candidateId?: number | null
): Promise<{ payload: API$Comment }> {
  return post(`/api/internal/profiles/${id}/comments/`, {
    data: { text, ...(candidateId ? { candidate_id: candidateId } : undefined) },
  });
}

export function commentsUpdate(
  id: number,
  commentId: number,
  text: string
): Promise<{ payload: API$Comment }> {
  return patch(`/api/internal/profiles/${id}/comments/${commentId}/`, { data: { text } });
}

export function commentsDelete(id: number, commentId: number): Promise<void> {
  return del(`/api/internal/profiles/${id}/comments/${commentId}/`);
}

export function deUpload(formData: FormData): Promise<{ payload: API$DEImport }> {
  return post('/api/internal/enrichment_requests/', {
    dataType: 'formData',
    data: formData,
  });
}

export function getDeImports(): Promise<{ payload: Array<API$DEImport> }> {
  return get('/api/internal/enrichment_requests/');
}

export function templatesGet(params?: API$TemplatesParams): Promise<{
  payload: API$PaginatedResponse<API$Template> & { example_templates: Array<API$ExampleTemplate> };
}> {
  return get('/api/internal/mailing_templates/', { ...params, messaging_type: 'mailing' });
}

export function mailingVariablesGet(): Promise<{ payload: Array<API$MailingVariable> }> {
  return get('/api/internal/mailing_variables/');
}

export function templateEdit(
  id: number,
  name?: string,
  body?: string,
  custom_variables?: Array<string>,
  access_type?: API$Folder['access_type'],
  subject?: string
): Promise<{ payload: API$Template }> {
  return patch(`/api/internal/mailing_templates/${id}/`, {
    data: { name, subject, body, custom_variables, access_type },
  });
}

export function templateCreate(
  name: string,
  body: string,
  custom_variables: Array<string> = [],
  access_type?: API$Folder['access_type'],
  subject?: string
): Promise<{ payload: API$Template }> {
  return post('/api/internal/mailing_templates/', {
    data: { name, subject, body, custom_variables, access_type },
  });
}

export function templateDelete(id: number): Promise<void> {
  return del(`/api/internal/mailing_templates/${id}/`);
}

export function templateGet(id: number): Promise<{ payload: API$Template }> {
  return get(`/api/internal/mailing_templates/${id}/`);
}

export function sendSingleMessage(
  data: API$SingleMessageData,
  params?: { launch?: boolean }
): Promise<{ payload: API$MailingList }> {
  return post('/api/internal/mailing_lists/', { data: { ...data }, params });
}

export function sendTestMessage(data: API$PreviewMessagesData): Promise<void> {
  return post('/api/internal/messaging/send_test_email/', { data });
}

export function createMailingList(data: API$CreateMailingListData): Promise<{
  payload: API$MailingList;
}> {
  return put('/api/internal/mailing_lists/', {
    data: { ...data, messaging_type: 'mailing' },
  });
}

export function editMailingList(
  mailingListId: number,
  data: {
    access_type?: API$Folder['access_type'];
    custom_variables?: Array<string>;
    name?: string;
    templates?: Array<API$MessageTemplate>;
  }
): Promise<{
  payload: API$MailingList;
}> {
  return patch(`/api/internal/mailing_lists/${mailingListId}/`, { data });
}

export function launchMailingList(
  mailingListId: number,
  data?: API$SendDelayInfo,
  additionalMailingData?: Record<string, any>
): Promise<{
  payload: {
    error_relaunched: number;
    launched: number;
    relaunched: number;
    without_email: number;
    without_variable_values: number;
  };
}> {
  return post(`/api/internal/mailing_lists/${mailingListId}/launch/`, {
    data: { ...data, ...additionalMailingData },
  });
}

export function deleteMailingList(
  mailingListId: number,
  params?: API$SaveMessagingHistory
): Promise<void> {
  return del(`/api/internal/mailing_lists/${mailingListId}/`, params);
}

export function stopMailingList(mailingListId: number): Promise<{
  payload: {
    stopped: number;
  };
}> {
  return post(`/api/internal/mailing_lists/${mailingListId}/stop/`);
}

export function getMailingRecipients(params?: API$MailingRecipientsParams): Promise<{
  payload: API$PaginatedResponse<API$MailingRecipient>;
}> {
  return get('/api/internal/mailing_recipients/', { ...params, messaging_type: 'mailing' });
}

export function updateMailingRecipient(
  recipientId: number,
  data: { [key: string]: any }
): Promise<{
  payload: API$MailingRecipient;
}> {
  return patch(`/api/internal/mailing_recipients/${recipientId}/`, { data });
}

export function getRecipientDetailed(
  recipientId: number
): Promise<{ payload: API$RecipientDetailed }> {
  return get(`/api/internal/mailing_recipients/${recipientId}/`);
}

export function usersGet(params?: API$UsersParams): Promise<{
  payload: API$PaginatedResponse<API$Assignee>;
}> {
  return get('/api/internal/users/', params);
}

export function getMessagePreview(
  data: API$PreviewMessagesData
): Promise<{ payload: Array<API$MessagePreview> }> {
  return post('/api/internal/messaging/preview/', { data });
}

export function exportEnrichedProfiles(id: number, params: API$DownloadFileParams): Promise<void> {
  return download(`/api/internal/enrichment_requests/${id}/results/`, { params });
}

export function getHMCompanyDisplaySectionsAsAdmin(): Promise<{
  payload: API$HMDisplaySections;
}> {
  return get('/api/internal/hiring_managers/settings/');
}

export function getHMCompanyDisplaySectionsAsManager(
  companyId: string
): Promise<{ payload: API$HMDisplaySections }> {
  return get(`/api/internal/companies/${companyId}/hiring_managers/settings/`);
}

export function updateHMCompanyDisplaySectionsAsAdmin(
  displaySections: Array<string>
): Promise<{ payload: API$HMDisplaySections }> {
  return patch(`/api/internal/hiring_managers/settings/`, {
    data: { hm_display_sections: displaySections },
  });
}

export function updateHMCompanyDisplaySectionsAsManager(
  companyId: string,
  displaySections: Array<string>
): Promise<{ payload: API$HMDisplaySections }> {
  return patch(`/api/internal/companies/${companyId}/hiring_managers/settings/`, {
    data: { hm_display_sections: displaySections },
  });
}

export function getProfileRealtimeDiff(
  profileId: number,
  companyId: number
): Promise<{ payload: API$ProfileRealtimeDiff }> {
  return get(`/api/internal/profiles/${profileId}/rt_diff/${companyId}`);
}

export function getMailingList(id: number): Promise<{ payload: API$MailingList }> {
  return get(`/api/internal/mailing_lists/${id}/`);
}

export function getRecipientPreview(
  recipientId: number
): Promise<{ payload: Array<API$MessagePreview> }> {
  return get(`/api/internal/mailing_recipients/${recipientId}/preview/`);
}

export function deleteRecipient(
  recipientId: number,
  params?: API$SaveMessagingHistory
): Promise<void> {
  return del(`/api/internal/mailing_recipients/${recipientId}/`, params);
}

export function addRecipientsToMailingList(
  mailingListId: number,
  candidateIds: Array<number>
): Promise<{
  payload: {
    added_candidate_ids: Array<number>;
    candidate_errors: { [key: number]: Array<string> };
    candidate_ids: Array<number>;
  };
}> {
  return put('/api/internal/mailing_recipients/', {
    data: { mailing_list_id: mailingListId, candidate_ids: candidateIds },
  });
}

export function resumeFragmentAdd(
  field: string,
  value: string,
  type: string,
  docid: number,
  orderId?: number
): Promise<{ payload: { resumeId: number; unitedProfile: API$Profile } }> {
  const html =
    field === 'userlinks'
      ? value
      : JSON.stringify({
          value,
          type: type.toUpperCase(),
        });
  const url = orderId === undefined ? `ah://fragment-${field}` : 'ah://ah-help';
  const data = { url, html, order_id: orderId };

  return post(`/api/internal/profiles/${docid}/private/`, { data });
}

export function resumeFragmentRemove(
  resume_id: string,
  docid: number,
  orderId?: number
): Promise<void> {
  const data = { order_id: orderId };

  return del(`/api/internal/profiles/${docid}/private/${resume_id}/`, null, data);
}

export function removeResume(id: string, profileId: number): Promise<void> {
  return del(`/api/internal/profiles/${profileId}/private/${id}/`);
}

export function getMailingListLaunchInfo(
  mailingListId: number
): Promise<{ payload: API$MailingListLaunchInfo }> {
  return get(`/api/internal/mailing_lists/${mailingListId}/launch_info/`);
}

export const saveProfile = (data: API$EditProfileData): Promise<{ payload: API$Profile }> =>
  post('/api/internal/profiles/private/', { data });

export function createProfileFromFile(file_id: number): Promise<{ payload: API$Profile }> {
  return put('/api/internal/profiles/private/', { data: { file_id } });
}

export function editProfile(
  profileId: number,
  data: API$EditProfileData
): Promise<{ payload: API$Profile }> {
  return patch(`/api/internal/profiles/private/${profileId}/`, { data });
}

export function enrichByFile(
  profileID: number,
  resumeId: string
): Promise<{ payload: API$Profile }> {
  return put(`/api/internal/profiles/private/${profileID}/`, { data: { resume_id: resumeId } });
}

export function getEmailSources(
  profile_id: number
): Promise<{ payload: { [key: string]: string } }> {
  return get('/api/internal/mailing_variables/email_sources/', { profile_id });
}

export function updateUserSettings(
  data: API$DataUserSettings
): Promise<{ payload: API$UserSettings }> {
  return patch(`/api/internal/user/settings/`, { data });
}

export function getActiveUsers(params: {
  system_part?: 'ext' | 'search';
}): Promise<{ payload: API$ActiveUsers }> {
  return get(`/api/internal/active-users/`, params);
}

export function getCompanyFormData(): Promise<{ payload: Array<API$CompanyFormData> }> {
  return get('/api/internal/companies/dropdowns/');
}

export function companyCreate(
  data: Partial<API$CompanyFormValues>
): Promise<{ payload: API$Company }> {
  return post('/api/internal/companies/new/', { data });
}

export function companiesGet(params?: API$CompaniesParams): Promise<{
  payload: API$PaginatedResponse<API$CompanyShort>;
}> {
  return get('/api/internal/companies/', params);
}

export function companyGet(id: string): Promise<{ payload: API$Company }> {
  return get(`/api/internal/companies/${id}/`);
}

export function getCompanyStatistics(
  id: number,
  params?: API$CompanyStatisticsQueryParams
): Promise<{ payload: API$CompanyStatistics }> {
  return get(`/api/internal/companies/${id}/statistics/`, params);
}
export function companyDelete(id: number): Promise<void> {
  return del(`/api/internal/companies/${id}/`);
}

export function companyEdit(
  id: number,
  data: Partial<API$CompanyFormValues>
): Promise<{ payload: API$Company }> {
  return put(`/api/internal/companies/${id}/`, { data });
}

export function employeeStatsGet(companyId: string): Promise<{ payload: API$EmployeeStats }> {
  return get(`/api/internal/companies/${companyId}/employees/stats/`);
}

export function companyCreditsAdd(companyId: string, data: API$CompanyCreditsData): Promise<void> {
  return post(`/api/internal/companies/${companyId}/credits/`, { data });
}

export function getProfilesViews(params: {
  creator_id: string;
  date_range?: string;
}): Promise<{ payload: API$ViewsHistory }> {
  return get('/api/profiles_events/', { ...params, only_viewed: true });
}

export function employeeGet(id: string | number): Promise<{ payload: API$Assignee }> {
  return get(`/api/internal/workers/${id}/`);
}

export function employeeAdd(
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return post('/api/internal/workers/', { data, params });
}

export function employeeEdit(
  id: string | number,
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return patch(`/api/internal/workers/${id}/`, { data, params });
}

export function employeeDelete(id: string | number): Promise<void> {
  return del(`/api/internal/workers/${id}/`);
}

export function getEmployeeDepartment({
  limit,
  offset,
}: {
  limit: number;
  offset: number;
}): Promise<{
  payload: {
    count: number;
    next: string | null;
    previous: string | null;
    results: Array<{
      id: number;
      name: string;
    }>;
  };
}> {
  return get('/api/internal/user_groups/', { limit, offset });
}

export function createEmployeeDepartment(name: string): Promise<{
  payload: {
    id: number;
    name: string;
  };
}> {
  return post('/api/internal/user_groups/', { data: { name } });
}

export function adminGet(id: string | number): Promise<{ payload: API$Assignee }> {
  return get(`/api/internal/admins/${id}/`);
}

export function adminAdd(
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return post('/api/internal/admins/', { data, params });
}

export function adminEdit(
  id: string | number,
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return patch(`/api/internal/admins/${id}/`, { data, params });
}

export function adminDelete(id: string | number): Promise<void> {
  return del(`/api/internal/admins/${id}/`);
}

export function hmGet(id: string | number): Promise<{ payload: API$Assignee }> {
  return get(`/api/internal/hiring_managers/${id}/`);
}

export function hmAdd(
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return post('/api/internal/hiring_managers/', { data, params });
}

export function hmEdit(
  id: string | number,
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return patch(`/api/internal/hiring_managers/${id}/`, { data, params });
}

export function hmDelete(id: string | number): Promise<void> {
  return del(`/api/internal/hiring_managers/${id}/`);
}

export function managersGet(): Promise<{ payload: Array<API$Assignee> }> {
  return get('/api/internal/managers/');
}

export function managerGet(id: string | number): Promise<{ payload: API$Assignee }> {
  return get(`/api/internal/managers/${id}/`);
}

export function managerAdd(
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return post('/api/internal/managers/', { data, params });
}

export function managerEdit(
  id: string | number,
  data: API$EmployeeFormData,
  params?: API$EmployeeFormParams
): Promise<{ payload: API$Assignee }> {
  return patch(`/api/internal/managers/${id}/`, { data, params });
}

export function managerDelete(id: string | number): Promise<void> {
  return del(`/api/internal/managers/${id}/`);
}

export function inviteResend(id: number): Promise<{ payload: API$Invitation }> {
  return post(`/api/internal/invitations/${id}/resend/`);
}

export function employeeSwitchRole(id: number): Promise<{ payload: API$Assignee }> {
  return patch(`/api/internal/workers/${id}/switch/`);
}

export function adminSwitchRole(id: number): Promise<{ payload: API$Assignee }> {
  return patch(`/api/internal/admins/${id}/switch/`);
}

export function employeeMoveToCompany(id: number | string, companyId: number): Promise<void> {
  return post(`/api/internal/workers/${id}/move/`, { data: { company_id: companyId } });
}

export function adminMoveToCompany(id: number | string, companyId: number): Promise<void> {
  return post(`/api/internal/admins/${id}/move/`, { data: { company_id: companyId } });
}

export function oauthConnectionsGet(): Promise<{ payload: Array<API$OAuthConnection> }> {
  return get('/api/internal/oauth/connections/');
}

export function oauthConnectionDelete(clientId: string): Promise<void> {
  return del(`/api/internal/oauth/connections/${clientId}/`);
}

export function userOAuthConnectionsGet(
  userId: string | number
): Promise<{ payload: Array<API$OAuthConnection> }> {
  return get(`/api/internal/oauth/users/${userId}/connections/`);
}

export function userOAuthConnectionDelete(
  userId: string | number,
  clientId: string
): Promise<void> {
  return del(`/api/internal/oauth/users/${userId}/connections/${clientId}/`);
}

export function getAtsFolders(): Promise<{ payload: Array<API$Folder> }> {
  return get('/api/internal/ats-folders/');
}

export function bansGet(params: {
  offset: number;
  q: string;
  status: 'active' | 'removed';
}): Promise<{ payload: { count: number; results: Array<API$ProfileBan> } }> {
  const query = new URLSearchParams({
    status: params.status,
    q: params.q,
    limit: '50',
    offset: String(params.offset),
  });

  return get(`/api/internal/banned_links/?${query}`);
}

export function banPreview(urls: Array<string>): Promise<{ payload: API$BanPreview }> {
  return post('/api/internal/banned_links/preview/', { data: { urls } });
}

export function banProfilePreview(profileId: number): Promise<{ payload: API$BanPreview }> {
  return get(`/api/internal/banned_links/profile_preview/?profile_id=${profileId}`);
}

export function banCreate(
  data: API$BanDetails & { url: string }
): Promise<{ payload: API$ProfileBan }> {
  return post('/api/internal/banned_links/', { data });
}

export function banProfile(
  data: API$BanDetails & { profile_id: number }
): Promise<{ payload: API$ProfileBan }> {
  return post('/api/internal/banned_links/profile/', { data });
}

export function bansImport(formData: FormData): Promise<{ payload: API$BanImportResult }> {
  return post('/api/internal/banned_links/import/', { dataType: 'formData', data: formData });
}

export function banRemove(id: number, reason: string): Promise<void> {
  return post(`/api/internal/banned_links/${id}/unban/`, { data: { reason } });
}

export function getExtensionNotifications(): Promise<{
  payload: Array<API$ExtensionNotification>;
}> {
  return get('/api/internal/notifications/');
}

export function createExtensionNotification(
  data: API$ExtensionNotification
): Promise<{ payload: API$ExtensionNotification }> {
  return post('/api/internal/notifications/', { data });
}

export function deleteExtensionNotification(id: number): Promise<void> {
  return del(`/api/internal/notifications/${id}/`);
}

export function editExtensionNotification(
  id: number,
  data: API$ExtensionNotification
): Promise<void> {
  return put(`/api/internal/notifications/${id}/`, { data });
}

export function getSystemNotifications(): Promise<{
  payload: API$PaginatedResponse<API$SystemNotification[]>;
}> {
  return get('/api/internal/system-notifications/?limit=100');
}

export function createSystemNotification(
  data: Partial<API$SystemNotification>
): Promise<{ payload: API$SystemNotification }> {
  return post('/api/internal/system-notifications/', { data });
}

export function deleteSystemNotification(id: number): Promise<void> {
  return del(`/api/internal/system-notifications/${id}/`);
}

export function editSystemNotification(
  id: number,
  data: Partial<API$SystemNotification>
): Promise<void> {
  return put(`/api/internal/system-notifications/${id}/`, { data });
}

export function setSystemNotificationStatus(
  id: AH$SystemNotification['id'],
  status: 'read' | 'hidden'
): Promise<any> {
  return post(`/api/internal/system-notifications/${id}/${status}/`, { data: {} });
}

export function getStatistics(
  section: AH$StatisticsSection,
  companyId: string,
  params: Record<string, string | Array<string>>
): Promise<{ payload: API$Statistics }> {
  return get(`/api/internal/companies/${companyId}/statistics/${section}/`, params);
}

export function getStatisticsFilters(
  section: AH$StatisticsSection,
  companyId: string,
  params: Record<string, string | Array<string>>
): Promise<{ payload: Record<string, Array<string>> }> {
  return get(`/api/internal/companies/${companyId}/statistics/${section}/filters/`, params);
}

export function getStatisticsSettings(
  section: AH$StatisticsSection,
  companyId: string
): Promise<{ payload: Record<string, Record<string, boolean>> }> {
  return get(`/api/internal/companies/${companyId}/statistics/${section}/settings/`);
}

export function setStatisticsSettings(
  section: AH$StatisticsSection,
  companyId: string,
  data: Record<string, Record<string, boolean>>
): Promise<{ payload: Record<string, Record<string, boolean>> }> {
  return post(`/api/internal/companies/${companyId}/statistics/${section}/settings/`, { data });
}

export function recaptchaSitekeyGet(): Promise<{ payload: { key: string } }> {
  return get('/api/internal/recaptcha-key/');
}

export function passwordRecover(
  data: API$RecoverPasswordData
): Promise<{ payload: API$RecoverPasswordPayload }> {
  return post('/api/internal/recover-password/', { data });
}

export function passwordRecoveryEmailGet(token: string): Promise<{ payload: { email: string } }> {
  return get(`/api/internal/reset-password/${token}/`);
}

export function passwordReset(token: string, data: API$ResetPasswordData): Promise<void> {
  return post(`/api/internal/reset-password/${token}/`, { data });
}

export function currentCompanyUpdate(
  data: API$CompanyUpdateData
): Promise<{ payload: API$CompanyUpdateData }> {
  return patch(`/api/internal/company/`, { data });
}

export function smartFolderQueryGet(
  folderId: number
): Promise<{ payload: API$SearchHistoryQuery }> {
  return get(`/api/internal/folders/${folderId}/search_query/`);
}

export function smartFolderHistoryGet(
  folderId: number,
  params?: API$PaginationParams
): Promise<{
  payload: API$PaginatedResponse<API$SearchHistoryQuery>;
}> {
  return get(`/api/internal/folders/${folderId}/search_history/`, params);
}

export function smartFoldersProfiles(
  params: string,
  data: API$SmartFoldersProfilesData
): Promise<{ pages: AH$Pages; payload: API$ProfilesPayload; profileReferer: string }> {
  return post(`/api/internal/smart_folders/profiles/?${params}`, { direct: true, data });
}

export function smartFoldersProfilesFilters(
  params: string,
  data: API$SmartFoldersProfilesData
): Promise<{
  gdpr: API$GdprFilters;
  payload: { [key: string]: Array<API$FilterValue> };
}> {
  return post(`/api/internal/smart_folders/profiles/filters/?${params}`, { direct: true, data });
}

export function leverVacancies(): Promise<{ payload: Array<API$IntegrationVacancy> }> {
  return get('/api/lever/vacancies/');
}

export function leverCandidateVacancies(
  ah_profile_id: string | number
): Promise<{ payload: API$IntegrationCandidateVacancies }> {
  return get('/api/lever/vacancies/', { ah_profile_id });
}

export function leverSelectCandidate(
  ah_profile_id: string | number,
  job_id?: string | number
): Promise<void> {
  return post('/api/lever/candidates/', { data: { ah_profile_id, job_id } });
}

export function leverGetUsers(): Promise<{ payload: Array<API$IntegrationUser> }> {
  return get('/api/lever/users/');
}

export function leverSelectUser(user_id: string): Promise<void> {
  return post('/api/lever/users/', { data: { user_id } });
}

export function companyCustomSettingsUpdate(
  companyId: number,
  data: Record<string, 0 | 1>
): Promise<{ payload: API$Company['custom_settings'] }> {
  return patch(`/api/internal/companies/${companyId}/custom_settings/`, { data });
}

export function greenhouseV3Authorize(): Promise<{ payload: API$GreenhouseV3Authorize }> {
  return get('/api/v6/greenhouse/auth/authorize/');
}

export function greenhouseV3Callback(params: {
  code: string;
  state?: string;
}): Promise<{ payload: API$GreenhouseV3Callback }> {
  return get('/api/v6/greenhouse/auth/callback', params);
}

export function connectTeamtailor(
  access_token: string,
  email: string
): Promise<{ payload: API$GreenhouseAuth }> {
  return post('/api/v6/teamtailor/auth/', { data: { access_token, email } });
}

export function createMergeMagicLink(
  link_expiry_mins?: number,
  integration?: string
): Promise<{ payload: { magic_link_url: string } }> {
  return post('/api/v6/merge/magic-link/', { data: { link_expiry_mins, integration } });
}

export function mergeGetUsers(refresh = false): Promise<{ payload: Array<API$MergeRemoteUser> }> {
  return get('/api/v6/merge/users/', refresh ? { refresh: 1 } : undefined);
}

export function mergeMapUser(
  user_id: number,
  merge_user_id: string
): Promise<{ payload: API$MergeRemoteUser }> {
  return post('/api/v6/merge/user-mapping/', { data: { user_id, merge_user_id } });
}

export function mergeUnmapUser(user_id: number): Promise<void> {
  return del(`/api/v6/merge/user-mapping/${user_id}/`);
}

export function connectSmartRecruitersImportProfiles(api_key: string): Promise<unknown> {
  return post('/api/smartrecruiters/import_candidates/', { data: { api_key } });
}

export function connectHunflowImportProfiles(
  connection_link: string,
  huntflow_premier: boolean,
  huntflow_api_url?: string
): Promise<unknown> {
  return post('/api/huntflow/import_candidates/', {
    data: { connection_link, huntflow_premier, huntflow_api_url },
  });
}

export function marketInsightsGet(
  params: string,
  folderId?: number
): Promise<{ payload: API$InsightsPayload }> {
  return post(`/api/internal/market_insights/?${params}`, {
    direct: true,
    ...(folderId ? { data: { folderId } } : undefined),
  });
}

export function checkIsMailingConnected(): Promise<any> {
  return post('/api/internal/messaging/send_test_email/', { data: { check_permissions: true } });
}

export function registerUser(data: API$RegisterUserData): Promise<void> {
  return post('/api/internal/register/', { data });
}

export function activateUser(token: string): Promise<void> {
  return get(`/api/internal/activate/${token}/`);
}

// MetaBase
export function sendMetric<T extends MetricsType>(
  type: T,
  event: $Values<(typeof metricsEventsMap)[T]>,
  params?: Record<string, any>
): Promise<void> {
  return get(`/api/internal/metrics/${type}/${event}/`, params);
}

export function findContacts(profileId: number): Promise<{ payload: API$Profile }> {
  return get(`/api/internal/profiles/${profileId}/get-contacts/`);
}

export function sequenceCreate(data: API$FolderData): Promise<void> {
  return post('/api/internal/sequences/', { data });
}

export function sequenceEdit(id: number, data: API$FolderData): Promise<void> {
  return patch(`/api/internal/sequences/${id}/`, { data });
}

export function sequenceGet(id: number): Promise<{ payload: API$Folder }> {
  return get(`/api/internal/sequences/${id}/`);
}

export function sequenceDelete(id: number, saveMessagingHistory?: boolean): Promise<void> {
  return del(`/api/internal/sequences/${id}/`, { saveMessagingHistory });
}

export function sequencesGet(params?: API$PaginationParams): Promise<{
  payload: API$PaginatedResponse<API$Folder & { mailing_stats: AH$MailingList['stats'] }>;
}> {
  return get(`/api/internal/sequences/`, params);
}

export function sequenceRecipientsGet(
  sequenceId: number,
  params: AH$CandidatesParams
): Promise<{
  payload: API$PaginatedResponse<
    API$MailingRecipient & {
      profile_info: API$MiniProfile;
      source: string;
      stats: {
        error: {
          restartable: number;
          unrestartable: number;
        };
        total_sent: number;
      };
    }
  >;
}> {
  return get(`/api/internal/sequences/${sequenceId}/recipients/`, params);
}

export function sequenceAddRecipient(
  sequenceId: number,
  profileData: API$SequenceProfileData
): Promise<void> {
  return post(`/api/internal/sequences/${sequenceId}/add_profile/`, { data: profileData });
}

export function sequenceRelaunchFailedMessages(
  sequenceId: number
): Promise<{ payload: { error_relaunched: number } }> {
  return post(`/api/internal/sequences/${sequenceId}/relaunch_failed/`);
}

export function gptVacanciesGet(): Promise<{ payload: API$GptVacancy[] }> {
  return get('/api/internal/vacancies/description/');
}

export function gptVacancyCreate(
  data: API$GptVacancyCreateData
): Promise<{ payload: API$GptVacancy }> {
  return post('/api/internal/vacancies/description/', { data });
}

export function gptVacancyUpdate(
  id: number,
  data: Partial<API$GptVacancyCreateData>
): Promise<{ payload: API$GptVacancy }> {
  return patch(`/api/internal/vacancies/description/${id}/`, { data });
}

export function gptVacancyDelete(id: number): Promise<void> {
  return del(`/api/internal/vacancies/description/${id}/`);
}

export function gptVacancyLanguagesGet(): Promise<{ payload: API$GptVacancyLanguages }> {
  return get('/api/internal/vacancies/description/languages/');
}

export function gptGenerationStart(
  profileId: number,
  vacancyId: number
): Promise<{ payload: { id: number } }> {
  return post('/api/internal/messaging/generation/', {
    data: { job_description: vacancyId, profile_id: profileId },
  });
}

export function gptGenerationGet(id: number): Promise<{ payload: API$GptGeneration }> {
  return get(`/api/internal/messaging/generation/${id}/`);
}

export function getSSOSettings(): Promise<{ payload: API$SSOFormValues }> {
  return get('/sso/settings/');
}

export function createSSOSettings(
  data: API$SSOFormValues
): Promise<{ payload: API$SSOFormValues }> {
  return post('/sso/settings/', { data });
}

export function updateSSOSettings(
  data: API$SSOFormValues
): Promise<{ payload: API$SSOFormValues }> {
  return patch('/sso/settings/', { data });
}

export function getQuotasCompany(companyId: number): Promise<{ payload: API$MonetizationQuota[] }> {
  return get(`/api/internal/companies/${companyId}/quotas/`);
}

export function deactivateQuotaCompany(companyId: number, quotaId: number): Promise<void> {
  return post(`/api/internal/companies/${companyId}/quotas/${quotaId}/deactivate/`);
}

export function addQuotaCompany(
  companyId: number,
  data?: { from_date: string; quota: number; till_date: string; used_quota: null | number }
): Promise<{ payload: API$MonetizationQuota }> {
  return post(`/api/internal/companies/${companyId}/quotas/`, {
    data: { ...data, is_active: true },
  });
}

export function verifyProfile(profileId: number): Promise<{
  payload: { companyName: string; eventDate: number; status: string; vacancyName: string };
}> {
  return get(`/api/internal/profiles/${profileId}/verifier/`);
}

export function telegramSendCode(phone_number: string): Promise<{ payload: AH$TelegramManager }> {
  return post('/api/internal/telegram/accounts/send_code_request/', { data: { phone_number } });
}

export function telegramConnectAccount({
  phone_number,
  code,
}: {
  code: string;
  phone_number: string;
}): Promise<{ payload: AH$TelegramManager }> {
  return post('/api/internal/telegram/accounts/', { data: { phone_number, code } });
}

export function getAllTelegramAccounts(params: AH$TelegramAccountsParams): Promise<{
  payload: {
    count: number;
    next: string | null;
    previous: string | null;
    results: AH$TelegramManager[];
  };
}> {
  return get('/api/internal/telegram/accounts/', params);
}

export function getTelegramAccount(id: string): Promise<{ payload: AH$TelegramManager }> {
  return get(`/api/internal/telegram/accounts/${id}`);
}

export function deleteTelegramAccount(id: number): Promise<void> {
  return del(`/api/internal/telegram/accounts/${id}/`);
}

export function updateTelegramAccount({
  id,
  limit_total,
}: {
  id: number;
  limit_total: number;
}): Promise<{ payload: AH$TelegramManager }> {
  return put(`/api/internal/telegram/accounts/${id}/`, { data: { limit_total } });
}

export function getTelegramDialogs(params: {
  created_by_id?: number;
  limit?: number;
  offset?: number;
  only_need_attention?: string;
  only_unread?: string;
  search?: string;
  telegram_account_id?: number;
}): Promise<{
  payload: { count: number; next: string; previous: string; results: AH$TelegramDialog[] };
}> {
  return get('/api/internal/telegram/dialogs/', params);
}

export function getTelegramDialogIdMessages({
  id,
  offset,
  limit,
}: {
  id: string;
  limit?: number;
  offset?: number;
}): Promise<{
  payload: {
    count: number;
    next: string | null;
    previous: string | null;
    results: AH$TelegramDialogMessage[];
  };
}> {
  return get(`/api/internal/telegram/dialogs/${id}/messages/`, { offset, limit });
}

export function getTelegramDialogId(id: string): Promise<{ payload: AH$TelegramDialog }> {
  return get(`/api/internal/telegram/dialogs/${id}/`);
}

export function createTelegramDialog(data: {
  username: string;
  profile_id?: number;
}): Promise<{ payload: AH$TelegramDialog }> {
  return post('/api/internal/telegram/dialogs/get_or_create/', { data });
}

export function sendTelegramMessage({
  message,
  dialogId,
}: {
  dialogId: string;
  message: string;
}): Promise<{ payload: AH$TelegramDialogMessage }> {
  return post(`/api/internal/telegram/dialogs/${dialogId}/send_message/`, { data: { message } });
}

export function telegramMarkAsReadMessage({
  dialogId,
  messageId,
}: {
  dialogId: number;
  messageId: number;
}): Promise<void> {
  return post(`/api/internal/telegram/dialogs/${dialogId}/mark_as_read/`, {
    data: { max_id: messageId },
  });
}

export async function downloadTelegramAttachment(
  dialog_id: number,
  message_id: number
): Promise<void> {
  return download(`/api/internal/telegram/dialogs/${dialog_id}/download_attachment/`, {
    params: {
      message_id,
    },
    method: 'GET',
    dataType: '',
  });
}
